/**
 * Facility Lesson Rates API Service
 *
 * CRUD operations for facility-level booking options (lesson rates).
 * API endpoints: /api/v2/facilities/{facilityId}/lesson_rates
 */
import {
  schedulingFetchDelete,
  schedulingFetchGet,
  schedulingFetchPatch,
  schedulingFetchPost,
} from "app/utils/request/scheduling/fetch";
import {
  LessonRateModel,
  LessonRateResponse,
} from "app/models/scheduling/LessonRateModel";

// Request payload for create/update (snake_case for API)
// Note: API expects `duration` as string (e.g. "30 mins", "1 hours")
// API returns `duration_in_minutes` for convenience but expects `duration` on create/update
export interface LessonRatePayload {
  title: string;
  duration: string; // e.g. "30 mins", "60 mins", "2 hours"
  rate: number;
  location?: string;
  description?: string | null;
  capacity?: number;
  default_rate?: boolean;
  active?: boolean;
  online_payments_active?: boolean;
  collect_type?: string;
  online_default?: boolean;
  bookings_limit?: number | null; // Maximum future bookings allowed (null = unlimited)
  hide_price?: boolean; // CN-2711: render the option's details without its price
  apply_to_existing?: boolean; // also re-apply duration/location to upcoming booked lessons
  // Calendar colour coding: a CALENDAR_COLORS value, or null for no colour.
  color?: string | null;
  schedule_resource_ids?: number[]; // the alternative resources; a booking takes one of them
  resource_category_ids?: number[]; // the sections whose resources are alternatives too
}

/**
 * GET /api/v2/facilities/{facilityId}/lesson_rates
 * Fetch all lesson rates for a facility
 */
export async function getFacilityLessonRates(
  facilityId: string
): Promise<LessonRateModel[]> {
  const response = await schedulingFetchGet<LessonRateResponse[]>(
    `facilities/${facilityId}/lesson_rates`
  );

  const rates = Array.isArray(response) ? response : [];
  return rates.map((rate) => new LessonRateModel(rate));
}

/**
 * GET /api/public/facilities/{facilityId}/lesson_rates
 * Fetch a facility's active booking options without authentication, for the
 * public availability calendar / landing pages (logged-out visitors).
 */
export async function getPublicFacilityLessonRates(
  facilityId: string
): Promise<LessonRateModel[]> {
  const response = await schedulingFetchGet<LessonRateResponse[]>(
    `facilities/${facilityId}/lesson_rates`,
    {},
    {},
    undefined,
    true
  );

  const rates = Array.isArray(response) ? response : [];
  return rates.map((rate) => new LessonRateModel(rate));
}

/**
 * POST /api/v2/facilities/{facilityId}/lesson_rates
 * Create a new lesson rate for a facility
 */
export async function createFacilityLessonRate(
  facilityId: string,
  payload: LessonRatePayload
): Promise<LessonRateModel> {
  const response = await schedulingFetchPost<LessonRateResponse>(
    `facilities/${facilityId}/lesson_rates`,
    payload
  );

  return new LessonRateModel(response);
}

/**
 * PATCH /api/v2/facilities/{facilityId}/lesson_rates/{rateId}
 * Update an existing lesson rate
 */
export async function updateFacilityLessonRate(
  facilityId: string,
  rateId: number,
  payload: Partial<LessonRatePayload>
): Promise<LessonRateModel> {
  const response = await schedulingFetchPatch<LessonRateResponse>(
    `facilities/${facilityId}/lesson_rates/${rateId}`,
    payload
  );

  return new LessonRateModel(response);
}

/**
 * DELETE /api/v2/facilities/{facilityId}/lesson_rates/{rateId}
 * Delete a lesson rate
 */
/**
 * POST /api/v2/facilities/{facilityId}/lesson_rates/sort
 * Update the order of lesson rates
 */
export async function sortFacilityLessonRates(
  facilityId: string,
  lessonRates: Record<number, number>
): Promise<void> {
  await schedulingFetchPost(
    `facilities/${facilityId}/lesson_rates/sort`,
    { lesson_rates: lessonRates }
  );
}

/**
 * DELETE /api/v2/facilities/{facilityId}/lesson_rates/{rateId}
 * Delete a lesson rate
 */
export async function deleteFacilityLessonRate(
  facilityId: string,
  rateId: number
): Promise<void> {
  await schedulingFetchDelete(
    `facilities/${facilityId}/lesson_rates/${rateId}`
  );
}
